import { useState, useEffect, useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { ScanLine, ChevronRight, ChevronLeft, Check, Sparkles, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/app/components/ui/card';
import { QuickAddSelect } from '@/components/shared/quick-add-select';
import { ScannerModal } from '@/components/scanner';
import { useGearModelSearch, useParseSerial, useCreateCustomGearModel } from '@/hooks/use-gear-models';
import { useCheckSerialNumber } from '@/hooks/use-assets';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { usePermissions } from '@/hooks/use-permissions';
import type {
  AssetType,
  CreateAssetPayload,
  GearModel,
} from '@/lib/types';

const ASSET_TYPE_OPTIONS: { value: AssetType; label: string }[] = [
  { value: 'harness', label: 'Harness' },
  { value: 'lanyard', label: 'Lanyard' },
  { value: 'connector', label: 'Connector' },
  { value: 'carabiner', label: 'Carabiner' },
  { value: 'rope', label: 'Rope' },
  { value: 'sling', label: 'Sling' },
  { value: 'descender', label: 'Descender' },
  { value: 'ascender', label: 'Ascender' },
  { value: 'pulley', label: 'Pulley' },
  { value: 'helmet', label: 'Helmet' },
  { value: 'anchor', label: 'Anchor' },
  { value: 'anchor_strap', label: 'Anchor Strap' },
  { value: 'fall_arrester', label: 'Fall Arrester' },
  { value: 'fall_arrest_accessory', label: 'Fall Arrest Accessory' },
  { value: 'mobile_fall_arrester', label: 'Mobile Fall Arrester' },
  { value: 'rigging_plate', label: 'Rigging Plate' },
  { value: 'rope_clamp', label: 'Rope Clamp' },
  { value: 'swivel', label: 'Swivel' },
  { value: 'other', label: 'Other' },
];

function InlineCreateGearModelForm({
  onCreated,
  onCancel,
}: {
  onCreated: (model: GearModel) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [manufacturerName, setManufacturerName] = useState('');
  const [assetCategory, setAssetCategory] = useState<AssetType>('other');
  const [error, setError] = useState('');
  const createGearModelMutation = useCreateCustomGearModel();

  const handleCreate = useCallback(async () => {
    const trimmedName = name.trim();
    const trimmedManufacturer = manufacturerName.trim();

    if (!trimmedManufacturer) {
      setError('Manufacturer is required');
      return;
    }

    if (!trimmedName) {
      setError('Model name is required');
      return;
    }

    setError('');
    try {
      const created = await createGearModelMutation.mutateAsync({
        manufacturer_name: trimmedManufacturer,
        name: trimmedName,
        asset_category: assetCategory,
      });
      onCreated(created);
    } catch (mutationError) {
      setError(mutationError instanceof Error ? mutationError.message : 'Failed to create custom gear model');
    }
  }, [assetCategory, createGearModelMutation, manufacturerName, name, onCreated]);

  return (
    <div className="space-y-4 py-2">
      <div className="space-y-2">
        <Label htmlFor="quick-gear-manufacturer-name">Manufacturer *</Label>
        <Input
          id="quick-gear-manufacturer-name"
          placeholder="Enter manufacturer"
          value={manufacturerName}
          onChange={(event) => setManufacturerName(event.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="quick-gear-model-name">Model name *</Label>
        <Input
          id="quick-gear-model-name"
          placeholder="Enter model name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="quick-gear-model-category">Asset category *</Label>
        <Select value={assetCategory} onValueChange={(value) => setAssetCategory(value as AssetType)}>
          <SelectTrigger id="quick-gear-model-category">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {ASSET_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {error ? <p className="text-sm text-red-500">{error}</p> : null}

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={createGearModelMutation.isPending}
        >
          Cancel
        </Button>
        <Button type="button" onClick={handleCreate} disabled={createGearModelMutation.isPending}>
          Create custom model
        </Button>
      </div>
    </div>
  );
}

const wizardSchema = z.object({
  type: z.enum(['carabiner', 'harness', 'rope', 'lanyard', 'sling', 'descender', 'ascender', 'pulley', 'helmet', 'anchor', 'anchor_strap', 'connector', 'fall_arrester', 'fall_arrest_accessory', 'mobile_fall_arrester', 'rigging_plate', 'rope_clamp', 'swivel', 'other'], {
    error: 'Type is required',
  }),
  gear_model_id: z.number().optional(),
  manufacturer: z.string().min(1, 'Manufacturer is required').max(128, 'Manufacturer must be less than 128 characters'),
  model: z.string().min(1, 'Model is required').max(128, 'Model must be less than 128 characters'),
  serial_number: z.string().min(1, 'Serial number is required').max(100, 'Serial number must be less than 100 characters'),
  date_of_manufacture: z.string().optional(),
  length_m: z.string().optional(),
  safe_working_load: z.string().optional(),
  inspection_interval_days: z.string().optional(),
});

type WizardFormValues = z.infer<typeof wizardSchema>;

interface CreateAssetWizardProps {
  onSubmit: (data: CreateAssetPayload) => void;
  isSubmitting?: boolean;
  onCancel?: () => void;
  initialSerialNumber?: string;
}

export function CreateAssetWizard({
  onSubmit,
  isSubmitting = false,
  onCancel,
  initialSerialNumber,
}: CreateAssetWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [modelSearchQuery, setModelSearchQuery] = useState('');
  const [selectedModel, setSelectedModel] = useState<GearModel | null>(null);
  const [useCustomModel, setUseCustomModel] = useState(false);
  const [domAutoDetected, setDomAutoDetected] = useState(false);
  const [domConfidence, setDomConfidence] = useState<'high' | 'medium' | 'low' | null>(null);
  const permissions = usePermissions();
  const canQuickAddGearModel = permissions.isAdmin;

  const debouncedSearchQuery = useDebouncedValue(modelSearchQuery, 300);
  const parseSerialMutation = useParseSerial();

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<WizardFormValues>({
    resolver: zodResolver(wizardSchema),
    defaultValues: {
      type: 'other',
      manufacturer: '',
      model: '',
      serial_number: initialSerialNumber || '',
      date_of_manufacture: '',
      length_m: '',
      safe_working_load: '',
      inspection_interval_days: '',
    },
  });

  const serialNumber = useWatch({ control, name: 'serial_number' }) ?? '';
  const manufacturer = useWatch({ control, name: 'manufacturer' }) ?? '';
  const selectedType = useWatch({ control, name: 'type' }) ?? 'other';
  const model = useWatch({ control, name: 'model' }) ?? '';

  const debouncedSerial = useDebouncedValue(serialNumber, 500);
  const { data: existingAsset, isLoading: isCheckingSerial } = useCheckSerialNumber(debouncedSerial);

  const { data: searchResults, isLoading: isSearching, isError: isSearchError, error: searchError } = useGearModelSearch(
    debouncedSearchQuery
  );

  const handleSelectModel = useCallback((model: GearModel) => {
    setSelectedModel(model);
    setUseCustomModel(false);
    setValue('gear_model_id', model.id);
    setValue('manufacturer', model.manufacturer_name);
    setValue('model', model.name);
    setValue('type', model.asset_category as AssetType);
    if (model.default_inspection_interval_days) {
      setValue('inspection_interval_days', model.default_inspection_interval_days.toString());
    }
    setModelSearchQuery('');
  }, [setValue]);

  const handleUseCustomModel = useCallback(() => {
    setSelectedModel(null);
    setUseCustomModel(true);
    setValue('gear_model_id', undefined);
    setValue('manufacturer', '');
    setValue('model', '');
    setValue('type', 'other');
  }, [setValue]);

  const parseSerialNumber = useCallback(async (serial: string, mfr: string) => {
    if (!serial || serial.length < 3) return;

    try {
      const result = await parseSerialMutation.mutateAsync({
        serial_number: serial,
        manufacturer: mfr,
      });

      if (result.date_of_manufacture) {
        setValue('date_of_manufacture', result.date_of_manufacture);
        setDomAutoDetected(true);
        setDomConfidence(result.confidence);
      }
    } catch {
      setDomAutoDetected(false);
      setDomConfidence(null);
    }
  }, [parseSerialMutation, setValue]);

  useEffect(() => {
    if (serialNumber && serialNumber.length >= 3 && manufacturer) {
      const timeoutId = setTimeout(() => {
        parseSerialNumber(serialNumber, manufacturer);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [serialNumber, manufacturer, parseSerialNumber]);

  const handleScanResult = (result: string) => {
    setValue('serial_number', result, { shouldValidate: true });
    setScannerOpen(false);
  };

  const handleFormSubmit = (values: WizardFormValues) => {
    const payload: CreateAssetPayload = {
      type: values.type,
      manufacturer: values.manufacturer,
      model: values.model,
      serial_number: values.serial_number,
    };

    if (values.gear_model_id) payload.gear_model = values.gear_model_id;
    if (values.date_of_manufacture) payload.date_of_manufacture = values.date_of_manufacture;
    if (values.length_m) payload.length_m = values.length_m;
    if (values.safe_working_load) payload.safe_working_load = values.safe_working_load;
    if (values.inspection_interval_days) {
      const interval = parseInt(values.inspection_interval_days, 10);
      if (!isNaN(interval)) payload.inspection_interval_days = interval;
    }

    onSubmit(payload);
  };

  const serialChecked = Boolean(debouncedSerial && debouncedSerial.length >= 2);
  const serialIsValid = serialNumber && serialNumber.length >= 1 && !existingAsset;
  const canProceedToStep2 = serialIsValid && serialChecked && !isCheckingSerial;
  const canProceedToStep3 = (selectedModel !== null || useCustomModel) && manufacturer && model;

  const goToNextStep = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    if (currentStep === 1 && canProceedToStep2) {
      setCurrentStep(2);
    } else if (currentStep === 2 && canProceedToStep3) {
      setCurrentStep(3);
    }
  };

  const goToPreviousStep = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const steps = [
    { number: 1, title: 'Serial Number', description: 'Enter or scan SN' },
    { number: 2, title: 'Model', description: 'Search catalog or enter' },
    { number: 3, title: 'Details', description: 'Review & confirm' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-8">
        {steps.map((step, index) => (
          <div key={step.number} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  currentStep > step.number
                    ? 'bg-green-500 text-white'
                    : currentStep === step.number
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {currentStep > step.number ? (
                  <Check className="h-5 w-5" />
                ) : (
                  step.number
                )}
              </div>
              <div className="mt-2 text-center">
                <p className={`text-sm font-medium ${currentStep === step.number ? 'text-blue-600' : 'text-gray-600'}`}>
                  {step.title}
                </p>
                <p className="text-xs text-gray-500 hidden sm:block">{step.description}</p>
              </div>
            </div>
            {index < steps.length - 1 && (
              <div className={`w-16 sm:w-24 h-0.5 mx-2 ${currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        {currentStep === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Serial Number</CardTitle>
              <CardDescription>Enter or scan the serial number to check inventory</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="serial_number">Serial Number *</Label>
                <div className="flex gap-2">
                  <Input
                    id="serial_number"
                    placeholder="Enter serial number"
                    {...register('serial_number')}
                    className="flex-1 text-lg font-mono"
                    autoFocus
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setScannerOpen(true)}
                    title="Scan barcode"
                  >
                    <ScanLine className="h-4 w-4" />
                  </Button>
                </div>
                {errors.serial_number && (
                  <p className="text-sm text-red-500">{errors.serial_number.message}</p>
                )}
              </div>

              {isCheckingSerial && serialNumber && serialNumber.length >= 2 && (
                <p className="text-sm text-gray-500">Checking inventory...</p>
              )}

              {existingAsset && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-5 w-5 text-yellow-600" />
                    <span className="font-medium text-yellow-800">Already in inventory</span>
                  </div>
                  <p className="text-sm text-yellow-700 mb-3">
                    Serial number &quot;{serialNumber}&quot; is already registered as{' '}
                    <span className="font-medium">{existingAsset.display_name}</span>.
                  </p>
                  <Button asChild variant="outline" size="sm">
                    <Link to={`/assets/${existingAsset.id}`}>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Asset
                    </Link>
                  </Button>
                </div>
              )}

              {serialChecked && !isCheckingSerial && !existingAsset && serialNumber && serialNumber.length >= 2 && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-blue-600" />
                    <span className="text-sm text-blue-700">
                      Asset not found in inventory. Do you wish to add?
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {currentStep === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Select Model</CardTitle>
              <CardDescription>
                Search the catalog by model name, manufacturer, or SKU
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isSearching && (
                <p className="text-sm text-gray-500">Searching...</p>
              )}

              {isSearchError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">
                    Error searching catalog: {(searchError as Error)?.message || 'Unknown error'}
                  </p>
                </div>
              )}

              {!selectedModel && !useCustomModel && (
                <div className="space-y-2">
                  <Label>Search Catalog</Label>
                  <QuickAddSelect<GearModel>
                    value={selectedModel}
                    onChange={(model) => {
                      if (model) {
                        handleSelectModel(model);
                      }
                    }}
                    options={searchResults ?? []}
                    getOptionValue={(model) => model.id}
                    getOptionLabel={(model) => model.name}
                    getOptionDescription={(model) =>
                      [
                        model.manufacturer_name,
                        model.model_number ? `SKU: ${model.model_number}` : '',
                        model.asset_category_display,
                      ]
                        .filter(Boolean)
                        .join(' • ')
                    }
                    searchQuery={modelSearchQuery}
                    onSearchQueryChange={setModelSearchQuery}
                    isLoading={isSearching}
                    placeholder="Type model name, e.g. Grillon, Am'D..."
                    quickAdd={
                      canQuickAddGearModel
                        ? {
                            triggerLabel: '+ New…',
                            dialogTitle: 'Create gear model',
                            dialogDescription: 'Create a catalog entry inline.',
                            renderForm: ({ onCreated, onCancel }) => (
                              <InlineCreateGearModelForm onCreated={onCreated} onCancel={onCancel} />
                            ),
                          }
                        : undefined
                    }
                  />
                </div>
              )}

              {selectedModel && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-800">Selected Model</span>
                  </div>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm text-green-700">
                    <dt className="text-green-600">Manufacturer:</dt>
                    <dd>{selectedModel.manufacturer_name}</dd>
                    <dt className="text-green-600">Model:</dt>
                    <dd>{selectedModel.name}</dd>
                    <dt className="text-green-600">Category:</dt>
                    <dd>{selectedModel.asset_category_display}</dd>
                    {selectedModel.color && (
                      <>
                        <dt className="text-green-600">Color:</dt>
                        <dd>{selectedModel.color}</dd>
                      </>
                    )}
                    {selectedModel.size && (
                      <>
                        <dt className="text-green-600">Size:</dt>
                        <dd>{selectedModel.size}</dd>
                      </>
                    )}
                    {selectedModel.default_inspection_interval_days && (
                      <>
                        <dt className="text-green-600">Inspection interval:</dt>
                        <dd>{selectedModel.default_inspection_interval_days} days</dd>
                      </>
                    )}
                  </dl>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedModel(null);
                      setValue('gear_model_id', undefined);
                      setValue('manufacturer', '');
                      setValue('model', '');
                      setValue('type', 'other');
                      setValue('inspection_interval_days', '');
                    }}
                    className="mt-2 text-green-700 hover:text-green-900"
                  >
                    Change model
                  </Button>
                </div>
              )}

              {!selectedModel && !useCustomModel && (
                <div className="border-t pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleUseCustomModel}
                  >
                    Model not in catalog? Enter manually
                  </Button>
                </div>
              )}

              {useCustomModel && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">Enter the model details manually:</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setUseCustomModel(false);
                        setValue('manufacturer', '');
                        setValue('model', '');
                        setValue('type', 'other');
                      }}
                    >
                      Back to search
                    </Button>
                  </div>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="type">Category *</Label>
                      <Select
                        value={selectedType}
                        onValueChange={(value) => setValue('type', value as AssetType)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {ASSET_TYPE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="manufacturer">Manufacturer *</Label>
                        <Input
                          id="manufacturer"
                          placeholder="Enter manufacturer"
                          {...register('manufacturer')}
                        />
                        {errors.manufacturer && (
                          <p className="text-sm text-red-500">{errors.manufacturer.message}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="model">Model *</Label>
                        <Input
                          id="model"
                          placeholder="Enter model"
                          {...register('model')}
                        />
                        {errors.model && (
                          <p className="text-sm text-red-500">{errors.model.message}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {currentStep === 3 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Review & Additional Details</CardTitle>
                <CardDescription>Confirm the information and add any optional details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {domAutoDetected && domConfidence && (
                  <div className={`flex items-center gap-2 p-3 rounded-lg ${
                    domConfidence === 'high' ? 'bg-green-50 text-green-700' :
                    domConfidence === 'medium' ? 'bg-yellow-50 text-yellow-700' :
                    'bg-gray-50 text-gray-700'
                  }`}>
                    <Sparkles className="h-4 w-4" />
                    <span className="text-sm">
                      Date of manufacture auto-detected ({domConfidence} confidence)
                    </span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="date_of_manufacture">
                    Date of Manufacture
                    {domAutoDetected && (
                      <span className="ml-2 text-xs text-green-600 font-normal">(Auto-detected)</span>
                    )}
                  </Label>
                  <Input
                    id="date_of_manufacture"
                    type="date"
                    {...register('date_of_manufacture')}
                    disabled={isSubmitting}
                    onChange={(e) => {
                      setValue('date_of_manufacture', e.target.value);
                      if (domAutoDetected) {
                        setDomAutoDetected(false);
                        setDomConfidence(null);
                      }
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Specifications</CardTitle>
                <CardDescription>Optional equipment specifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="length_m">Length (meters)</Label>
                    <Input
                      id="length_m"
                      type="number"
                      step="0.01"
                      placeholder="Enter length"
                      {...register('length_m')}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="safe_working_load">Safe Working Load (kg)</Label>
                    <Input
                      id="safe_working_load"
                      type="number"
                      step="0.01"
                      placeholder="Enter SWL"
                      {...register('safe_working_load')}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inspection_interval_days">
                    Inspection Interval (days)
                    {selectedModel?.default_inspection_interval_days && (
                      <span className="ml-2 text-xs text-blue-600 font-normal">
                        (Pre-filled from catalog)
                      </span>
                    )}
                  </Label>
                  <Input
                    id="inspection_interval_days"
                    type="number"
                    placeholder="Enter interval"
                    {...register('inspection_interval_days')}
                    disabled={isSubmitting}
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="text-lg">Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <dt className="text-gray-500">Serial Number:</dt>
                  <dd className="font-medium font-mono">{serialNumber}</dd>
                  <dt className="text-gray-500">Manufacturer:</dt>
                  <dd className="font-medium">{manufacturer}</dd>
                  <dt className="text-gray-500">Model:</dt>
                  <dd className="font-medium">{model}</dd>
                  <dt className="text-gray-500">Category:</dt>
                  <dd className="font-medium">{ASSET_TYPE_OPTIONS.find((o) => o.value === selectedType)?.label}</dd>
                  {selectedModel && (
                    <>
                      <dt className="text-gray-500">Source:</dt>
                      <dd className="font-medium text-green-600">From catalog</dd>
                    </>
                  )}
                </dl>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="flex justify-between mt-6">
          <div>
            {currentStep > 1 && (
              <Button type="button" variant="outline" onClick={goToPreviousStep}>
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
            {currentStep === 1 && onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>

          <div>
            {currentStep < 3 ? (
              <Button
                type="button"
                onClick={goToNextStep}
                disabled={
                  (currentStep === 1 && !canProceedToStep2) ||
                  (currentStep === 2 && !canProceedToStep3)
                }
              >
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Asset'}
              </Button>
            )}
          </div>
        </div>
      </form>

      <ScannerModal
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onScan={handleScanResult}
        title="Scan Serial Number"
        description="Scan a barcode to fill in the serial number"
      />
    </div>
  );
}
